import React, { useState } from 'react';

// helpers
import styled from 'styled-components';
import useTranslation from 'hooks/useTranslation';
import { StateModel } from 'redux/reducers';
import { colorsTheme } from 'resources/theme/styled/colors';
import { IApplication } from 'typings/application/applications';
import { IntakeFormData } from 'redux/reducers/intakeForm';
import { selectIntakeForm } from 'redux/actions/intakeForm';
import { selectApplication } from 'redux/actions/applications';
import { useDispatch, useSelector } from 'react-redux';

// components
import { AsyncButton } from '@ui';
import { Col, Row, Text } from '@ui';

const SelectAccessContext = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['auth', 'common', 'connections']);
  const [selectedContext, setSelectedContext] = useState<
    | {
        data: IApplication;
        type: 'client-group';
      }
    | {
        data: IntakeFormData;
        type: 'intake-form';
      }
    | null
  >(null);

  const applications = useSelector<StateModel, IApplication[]>(
    (state) => state.applications.applications || [],
  );
  const intakeSubmissions = useSelector<StateModel, IntakeFormData[]>(
    (state) => state.intakeForm.intakeSubmissions || [],
  );
  const userName = useSelector<StateModel>(
    (state) => state.auth.profileData?.name || '',
  );

  const selectClientGroup = (application: IApplication) => {
    setSelectedContext({
      data: application,
      type: 'client-group',
    });
  };

  const selectIntakeSubmission = (intakeForm: IntakeFormData) => {
    setSelectedContext({
      data: intakeForm,
      type: 'intake-form',
    });
  };

  const onContinue = () => {
    if (selectedContext) {
      if (selectedContext.type === 'client-group') {
        dispatch(selectApplication(selectedContext.data, true));
      } else if (selectedContext.type === 'intake-form') {
        dispatch(selectIntakeForm(selectedContext.data._id));
      }
    }
  };

  return (
    <>
      <StyledTextWithBottomMargin color={colorsTheme.colorWhite} variant="h4">
        {t('select_context.greeting', { userName })}
      </StyledTextWithBottomMargin>
      <StyledTextWithBottomMargin color={colorsTheme.colorLightD1}>
        {t('select_context.description')}
      </StyledTextWithBottomMargin>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Text
            color={colorsTheme.colorWhite}
            variant="h5"
            gutterBottom
            gutterTop
          >
            {t('select_context.client_groups')}
          </Text>
          <Row gutter={[16, 16]}>
            {applications?.map((application, index) => {
              return (
                <Col span={24} key={index}>
                  <ClientGroupCard
                    onClick={() => selectClientGroup(application)}
                    isSelected={application._id === selectedContext?.data._id}
                  >
                    <Text color={colorsTheme.colorWhite}>
                      {application.clientGroup.clientGroupName}
                    </Text>
                    <Text>{`${t(`open_an_account_new.account_type.application_types.${application.crmItemType}`)} | ${application.onboardingProcess?.item?.keyRelationshipInstance?.name || ''}`}</Text>
                  </ClientGroupCard>
                </Col>
              );
            })}
          </Row>
        </Col>
        {intakeSubmissions.length ? (
          <Col span={24}>
            <Text
              color={colorsTheme.colorWhite}
              variant="h5"
              gutterBottom
              gutterTop
            >
              {t('select_context.applications')}
            </Text>
            <Row gutter={[16, 16]}>
              {intakeSubmissions.map((intakeSubmission, index) => {
                return (
                  <Col span={24} key={index}>
                    <ClientGroupCard
                      onClick={() => selectIntakeSubmission(intakeSubmission)}
                      isSelected={
                        intakeSubmission._id === selectedContext?.data?._id
                      }
                    >
                      <Text color={colorsTheme.colorWhite}>
                        {intakeSubmission.directClientName
                          ? intakeSubmission.directClientName
                          : t('select_context.application_id_name', {
                              applicationNumber: intakeSubmission.hrid,
                            })}
                      </Text>
                      <Text>
                        {`${t(`applications.types.${intakeSubmission.type}`, {
                          ns: 'connections',
                        })} | ${intakeSubmission.keyRelationshipTemplateName || ''}`}
                      </Text>
                      <Text>
                        {t('select_context.application_status', {
                          status: t(
                            `applications.state.${intakeSubmission.state}`,
                            { ns: 'connections' },
                          ),
                        })}
                      </Text>
                    </ClientGroupCard>
                  </Col>
                );
              })}
            </Row>
          </Col>
        ) : null}
      </Row>
      <StyledAsyncButton
        disabled={!selectedContext}
        size="large"
        onClick={onContinue}
      >
        {t('continue', { ns: 'common' })}
      </StyledAsyncButton>
    </>
  );
};

const StyledTextWithBottomMargin = styled(Text)`
  margin-bottom: ${({ theme }) => theme.marginSm};
`;

const StyledAsyncButton = styled(AsyncButton)`
  width: 200px;
  margin-top: ${({ theme }) => theme.marginMd};
`;

const ClientGroupCard = styled.div<{ isSelected: boolean }>`
  padding: ${({ theme }) => theme.marginSm};
  background: ${({ theme }) => theme.colorDarkL2};
  cursor: pointer;
  border: 1px solid transparent;
  border-color: ${({ isSelected, theme }) =>
    isSelected ? theme.colorLightD1 : 'transparent'};
  &:hover {
    border-color: ${({ theme }) => theme.colorLightD1};
  }
`;

export default SelectAccessContext;
